import { MouseEvent, useState, useContext } from "react";
// @mui
import {
  TableCell,
  TableRow,
  Typography,
  Box,
  useTheme,
  MenuItem,
  Link,
} from "@mui/material";
import BrandAvatar from "~src/components/BrandAvatar";

// @types
import { FullBrand } from "~src/types/brand";
import ScaleCell from "./ScoreCell";
import Iconify from "~src/components/Iconify";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import { BrandComparisonContext } from "~src/contexts/BrandComparisonContext";
import { BrandResult } from "~src/sections/competitorAnalytics/SearchBar";
import { PATH_BRANDS } from "~src/routes/paths";

// ----------------------------------------------------------------------

type Props = {
  index: number;
  row: FullBrand;
  columns: string[];
  onSelectRow: VoidFunction;
  isPreFilteredMode?: boolean;
};

export default function BrandRow({
                                   row,
                                   columns,
                                   onSelectRow,
                                   index,
                                   isPreFilteredMode,
                                 }: Props) {
  // Call all hooks unconditionally at the top of the component.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const theme = useTheme();
  const { setSelectedBrands } = useContext(BrandComparisonContext) || {};
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [clickedBrand, setClickedBrand] = useState<any>(null);

  // Only check for essential fields
  if (!row.brand_id || !row.brandname) {
    return null;
  }

  const toggleExpand = (index: number) => {
    setExpandedIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>, brand: any) => {
    setAnchorEl(event.currentTarget);
    setClickedBrand(brand);
  };

  const handleAddBrand = () => {
    if (clickedBrand && setSelectedBrands) {
      setSelectedBrands((previousBrands) => {
        const brandExists = previousBrands.some(
          (b) => b.id === String(clickedBrand.brand_id)
        );
        if (brandExists) {
          return previousBrands;
        } else {
          const newBrand: BrandResult = {
            id: String(clickedBrand.brand_id),
            brand_name: clickedBrand.brandname,
            image: clickedBrand.profileimage,
          };
          return [...previousBrands, newBrand];
        }
      });
    }
    handleClose();
  };

  return (
    <TableRow
      sx={{
        borderBottom: (theme) => `solid 1px ${theme.palette.grey[200]}`,
      }}
    >
      <TableCell sx={{ cursor: "pointer" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isPreFilteredMode && (
            <>
              <IconButton
                id="basic-button"
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={(event) => handleClick(event, row)}
              >
                <Iconify
                  icon={"iconamoon:menu-kebab-vertical-bold"}
                  width={20}
                  height={20}
                />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  sx: {
                    boxShadow: "none",
                    border: `1px solid ${theme.palette.grey[200]}`,
                  },
                }}
                slotProps={{
                  list: {
                    "aria-labelledby": "basic-button",
                  }
                }}
              >
                <MenuItem onClick={handleAddBrand}>
                  <Iconify
                    icon={"pajamas:comparison"}
                    sx={{ mr: 1 }}
                    width={20}
                    height={20}
                  />{" "}
                  Add for Comparison
                </MenuItem>
              </Menu>
            </>
          )}
          <Link
            href={PATH_BRANDS.overview(row.brand_id)}
            onClick={(e) => {
              // If CMD (Mac) or Ctrl (Windows/Linux) is pressed, let the default behavior happen (open in new tab)
              if (e.metaKey || e.ctrlKey) {
                return;
              }
              // Otherwise, prevent default and navigate within the app
              e.preventDefault();
              onSelectRow();
            }}
            sx={{ 
              display: "flex", 
              alignItems: "center",
              textDecoration: 'none',
              color: 'inherit' 
            }}
          >
            <BrandAvatar
              brandName={row.brandname}
              profileImage={row.profileimage}
              size={40}
              sx={{
                mr: 2,
              }}
            />
            <Typography variant="subtitle2" noWrap>
              {row.brandname}
            </Typography>
          </Link>
        </Box>
      </TableCell>
      <ScaleCell
        scorePercentage={row.scaling_score_percentage}
        scoreRanking={row.scaling_score_ranking}
      />
      <ScaleCell
        scorePercentage={row.revenue_score_percentage}
        scoreRanking={row.revenue_score_ranking}
      />
      <TableCell sx={{ minWidth: 220, maxWidth: 220 }}>
        <Box sx={{ display: "flex" }}>
          <Box>
            {expandedIndex === index
              ? row.brand_description
              : row.brand_description?.substring(0, 20)}
            {row.brand_description && expandedIndex !== index && "..."}
          </Box>
          <Box>
            {row.brand_description && row.brand_description.length > 20 && (
              <span
                onClick={() => toggleExpand(index)}
                style={{
                  cursor: "pointer",
                  color: theme.palette.primary.dark,
                }}
              >
                {expandedIndex === index ? (
                  <Iconify
                    icon={"material-symbols-light:expand-circle-up-outline"}
                    width={20}
                    height={20}
                  />
                ) : (
                  <Iconify
                    icon={"material-symbols-light:expand-circle-down-outline"}
                    width={20}
                    height={20}
                  />
                )}
              </span>
            )}
          </Box>
        </Box>
      </TableCell>
      {columns.includes("fblikes") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {!row.fblikes && row.fblikes < 0
              ? 0
              : row.fblikes?.toLocaleString()}
          </Typography>
        </TableCell>
      )}
      {columns.includes("instagram_followers") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {!row.instagram_followers || row.instagram_followers < 0
              ? 0
              : row.instagram_followers?.toLocaleString()}
          </Typography>
        </TableCell>
      )}
      {columns.includes("fbads_count") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.fbads_count?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("latest_shopifyreviews_count") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.latest_shopifyreviews_count?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("latest_numberreviews") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.latest_numberreviews?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("latest_fbreviews") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.latest_fbreviews?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("latest_redditgrowth") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.latest_redditgrowth?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("estimated_monthly_traffic") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.store_data?.estimated_monthly_traffic?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("estimated_monthly_orders") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.store_data?.estimated_monthly_orders?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("estimated_monthly_revenue") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.store_data?.estimated_monthly_revenue?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("count") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.count?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("product_data.min_price") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.min_price?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("min_price") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.min_price?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("avg_price") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.avg_price?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("max_price") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.max_price?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
      {columns.includes("last_30_days") && (
        <TableCell>
          <Typography variant="body2" noWrap>
            {row.product_data?.last_30_days?.toLocaleString() || "-"}
          </Typography>
        </TableCell>
      )}
    </TableRow>
  );
}
